import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { Button, Input, Modal, message } from "antd";
import cn from "classnames";
import dayjs from "dayjs";
import Page from "../Page";
import DataTable, { DataColumn } from "../DataTable";
import BasicButton from "../../BasicButton";
import {
  APPROVE_CRYPTO_TRANSFER,
  ATTACH_CRYPTO_TRANSFER,
  GET_CRYPTO_TRANSFERS,
  REJECT_CRYPTO_TRANSFER,
} from "../../api/graphql/crypto";
import dialog from "../../styles/dialog.module.scss";
import ModeControl from "./ModeControl";
import CountUp from "../../motion/CountUp";
import styles from "./styles.module.scss";

/**
 * Crypto payments: every USDT (TRC20) transfer that arrived on the Ledger
 * address, the top-up it paid and the AML screen of its sender. A flagged
 * transfer waits here for an admin; one whose amount matched no top-up is
 * attached to the right one by hand.
 */

type AmlCheck = {
  name: string;
  status: "clean" | "flagged" | "error" | "skipped";
  detail: string | null;
  score: number | null;
  reportUrl: string | null;
  checkedAt: string | null;
};

type AmlStatus = "PENDING" | "CLEAN" | "FLAGGED" | "APPROVED" | "REJECTED";

type Transfer = {
  id: number;
  txHash: string;
  explorerUrl: string;
  /** Paid on the TRON Nile testnet while the mode was Test. */
  testnet: boolean;
  network: string;
  asset: string;
  fromAddress: string;
  toAddress: string;
  amount: string;
  blockTimestamp: string;
  amlStatus: AmlStatus;
  amlChecks: AmlCheck[];
  amlRiskScore: number | null;
  amlCheckedAt: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  payment: {
    id: number;
    hashId: string;
    status: "PENDING" | "PAID" | "FAIL" | "CANCEL";
    creditedUsd: number | null;
    requestedUsd: number;
    expectedAmount: string;
    userId: number;
    userEmail: string | null;
  } | null;
};

const PAGE = 100;

const FILTERS: {
  value: string;
  label: string;
  variables: { amlStatus?: AmlStatus; unmatched?: boolean };
  empty: string;
}[] = [
  {
    value: "all",
    label: "All",
    variables: {},
    empty: "No USDT has arrived on the Ledger address yet.",
  },
  {
    value: "review",
    label: "Needs review",
    variables: { amlStatus: "FLAGGED" },
    empty: "No transfer waits for a decision.",
  },
  {
    value: "unmatched",
    label: "Unmatched",
    variables: { unmatched: true },
    empty: "Every transfer found its top-up.",
  },
  {
    value: "unscreened",
    label: "Not screened",
    variables: { amlStatus: "PENDING" },
    empty: "Every sender has been screened.",
  },
];

const AML_LABEL: Record<AmlStatus, string> = {
  PENDING: "Not screened",
  CLEAN: "Clear",
  FLAGGED: "Flagged",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

const AML_TONE: Record<AmlStatus, string> = {
  PENDING: styles.toneNeutral,
  CLEAN: styles.toneSuccess,
  FLAGGED: styles.toneWarning,
  APPROVED: styles.toneSuccess,
  REJECTED: styles.toneError,
};

const CHECK_NAME: Record<string, string> = {
  tether_blacklist: "Tether",
  ofac_sanctions: "OFAC",
  risk_score: "Risk",
};

const PAYMENT_LABEL: Record<string, string> = {
  PAID: "Credited",
  PENDING: "Not credited",
  CANCEL: "Expired",
  FAIL: "Rejected",
};

const short = (value: string, head = 6, tail = 4) =>
  value.length > head + tail + 1
    ? `${value.slice(0, head)}…${value.slice(-tail)}`
    : value;

const usd = (value: number) =>
  `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const addressUrl = (transfer: Transfer) =>
  transfer.explorerUrl.replace(
    /transaction\/.*$/,
    `address/${transfer.fromAddress}`,
  );

/** "Tether clear · OFAC clear · Risk 12 (Low)" */
const checksSummary = (checks: AmlCheck[]) =>
  checks
    .filter((check) => check.status !== "skipped")
    .map((check) => {
      const name = CHECK_NAME[check.name] ?? check.name;
      if (check.name === "risk_score" && check.status !== "error") {
        return `${name} ${check.detail?.split(";")[0] ?? check.score}`;
      }
      return `${name} ${
        check.status === "clean"
          ? "clear"
          : check.status === "error"
            ? "n/a"
            : check.status
      }`;
    })
    .join(" · ");

type DialogKind = "approve" | "reject" | "attach";

const Actions: React.FC<{ transfer: Transfer; onDone: () => void }> = ({
  transfer,
  onDone,
}) => {
  const [open, setOpen] = useState<DialogKind | null>(null);
  const [paymentId, setPaymentId] = useState("");
  const [approve, { loading: approving }] = useMutation(
    APPROVE_CRYPTO_TRANSFER,
  );
  const [reject, { loading: rejecting }] = useMutation(REJECT_CRYPTO_TRANSFER);
  const [attach, { loading: attaching }] = useMutation(ATTACH_CRYPTO_TRANSFER);

  const undecided =
    transfer.amlStatus === "FLAGGED" || transfer.amlStatus === "PENDING";
  const attachable = !transfer.payment && transfer.amlStatus !== "REJECTED";
  if (!undecided && !attachable) return <span className={styles.muted}>—</span>;

  const run = async (kind: DialogKind) => {
    try {
      if (kind === "approve") {
        await approve({ variables: { id: transfer.id } });
        message.success(
          transfer.payment
            ? `${transfer.amount} USDT credited to ${transfer.payment.userEmail}`
            : "Transfer released; attach it to a top-up to credit it",
        );
      } else if (kind === "reject") {
        await reject({ variables: { id: transfer.id } });
        message.success("Transfer rejected, nothing credited");
      } else {
        await attach({
          variables: { id: transfer.id, paymentHashId: paymentId.trim() },
        });
        message.success(`Transfer attached to payment ${paymentId.trim()}`);
        setPaymentId("");
      }
      setOpen(null);
      onDone();
    } catch (error: any) {
      message.error(error?.message || "The action failed");
    }
  };

  const copy: Record<DialogKind, { title: string; text: string; ok: string }> =
    {
      approve: {
        title: `Approve ${transfer.amount} USDT`,
        text: transfer.payment
          ? `The money is credited to ${transfer.payment.userEmail} (payment ${transfer.payment.hashId}) right away.`
          : "The transfer is released but belongs to no top-up yet: attach it to one to credit it.",
        ok: transfer.payment ? "Approve and credit" : "Approve",
      },
      reject: {
        title: `Reject ${transfer.amount} USDT`,
        text: "Nothing is credited and the top-up fails. The USDT stays on the Ledger: sending it back is a manual transfer from the device.",
        ok: "Reject",
      },
      attach: {
        title: `Attach ${transfer.amount} USDT`,
        text: "The ID of the client's unpaid crypto top-up: they see it in the payment window and as ID number under Balance → Top-ups; the Telegram alert of the top-up has it too. The money is credited once the sender has passed AML.",
        ok: "Attach",
      },
    };

  return (
    <span className={styles.actions}>
      {undecided && (
        <>
          <button
            type="button"
            className={styles.action}
            onClick={() => setOpen("approve")}
          >
            Approve
          </button>
          <button
            type="button"
            className={cn(styles.action, styles.actionDanger)}
            onClick={() => setOpen("reject")}
          >
            Reject
          </button>
        </>
      )}
      {attachable && (
        <button
          type="button"
          className={styles.action}
          onClick={() => setOpen("attach")}
        >
          Attach
        </button>
      )}

      {open && (
        <Modal
          open
          title={<span className={dialog.title}>{copy[open].title}</span>}
          onCancel={() => setOpen(null)}
          width={480}
          footer={[
            <div key="footer" className={dialog.footer}>
              <Button className={dialog.cancel} onClick={() => setOpen(null)}>
                Back
              </Button>
              <Button
                className={open === "reject" ? dialog.delete : undefined}
                type="primary"
                loading={approving || rejecting || attaching}
                disabled={open === "attach" && !paymentId.trim()}
                onClick={() => run(open)}
              >
                {copy[open].ok}
              </Button>
            </div>,
          ]}
        >
          <p className={styles.modalText}>{copy[open].text}</p>
          {open === "attach" && (
            <>
              <label className={dialog.label} htmlFor={`attach-${transfer.id}`}>
                Payment ID
              </label>
              <Input
                id={`attach-${transfer.id}`}
                value={paymentId}
                onChange={(event) => setPaymentId(event.target.value)}
                placeholder="e.g. 912b9a"
                maxLength={32}
              />
            </>
          )}
        </Modal>
      )}
    </span>
  );
};

const CryptoPayments: React.FC = () => {
  const [filter, setFilter] = useState(FILTERS[0].value);
  const [limit, setLimit] = useState(PAGE);
  const active =
    FILTERS.find((option) => option.value === filter) ?? FILTERS[0];

  const { data, loading, error, refetch } = useQuery(GET_CRYPTO_TRANSFERS, {
    variables: { ...active.variables, offset: 0, limit },
    fetchPolicy: "cache-and-network",
  });
  const transfers: Transfer[] = data?.cryptoTransfers?.items ?? [];
  const total: number = data?.cryptoTransfers?.total ?? 0;
  const refresh = () => {
    refetch().catch(() => undefined);
  };

  const columns: DataColumn<Transfer>[] = [
    {
      key: "date",
      title: "Arrived",
      width: 150,
      fixed: true,
      render: (transfer) => (
        <time
          dateTime={transfer.blockTimestamp}
          title={dayjs(transfer.blockTimestamp).format("DD.MM.YYYY HH:mm:ss")}
        >
          {dayjs(transfer.blockTimestamp).format("DD.MM.YYYY HH:mm")}
        </time>
      ),
      sorter: (a, b) =>
        dayjs(a.blockTimestamp).valueOf() - dayjs(b.blockTimestamp).valueOf(),
    },
    {
      key: "amount",
      title: "Received",
      width: 160,
      mobile: "title",
      render: (transfer) => (
        <span className={styles.num}>
          {transfer.testnet && (
            <span
              className={cn(styles.badge, styles.toneWarning, styles.testBadge)}
              title="TRON Nile testnet: test USDT, not real money"
            >
              Test
            </span>
          )}
          {transfer.amount} {transfer.asset}
        </span>
      ),
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
    },
    {
      key: "credited",
      title: "Credited",
      width: 100,
      render: (transfer) =>
        transfer.payment?.creditedUsd != null ? (
          <span className={styles.num}>
            {usd(transfer.payment.creditedUsd)}
          </span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      key: "client",
      title: "Client",
      width: 200,
      render: (transfer) =>
        transfer.payment ? (
          <Link to={`/user/${transfer.payment.userId}`} className={styles.link}>
            {transfer.payment.userEmail ?? `user #${transfer.payment.userId}`}
          </Link>
        ) : (
          <span className={styles.warningText}>No matching top-up</span>
        ),
    },
    {
      key: "payment",
      title: "Payment",
      width: 150,
      render: (transfer) =>
        transfer.payment ? (
          <span
            className={styles.stack}
            title={`Asked for ${transfer.payment.expectedAmount} USDT ($${transfer.payment.requestedUsd})`}
          >
            <span className={styles.mono}>{transfer.payment.hashId}</span>
            <span className={styles.muted}>
              {PAYMENT_LABEL[transfer.payment.status] ??
                transfer.payment.status}
            </span>
          </span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      key: "aml",
      title: "AML",
      width: 240,
      mobile: "full",
      render: (transfer) => {
        const report = transfer.amlChecks.find((check) => check.reportUrl);
        return (
          <span className={styles.stack}>
            <span className={cn(styles.badge, AML_TONE[transfer.amlStatus])}>
              {AML_LABEL[transfer.amlStatus]}
            </span>
            <span className={styles.muted}>
              {checksSummary(transfer.amlChecks) || "—"}
              {report?.reportUrl && (
                <>
                  {" · "}
                  <a
                    href={report.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    Report ↗
                  </a>
                </>
              )}
            </span>
          </span>
        );
      },
    },
    {
      key: "actions",
      title: "Decision",
      width: 190,
      mobile: "footer",
      render: (transfer) => <Actions transfer={transfer} onDone={refresh} />,
    },
    {
      key: "from",
      title: "From",
      width: 130,
      render: (transfer) => (
        <a
          href={addressUrl(transfer)}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(styles.link, styles.mono)}
          title={transfer.fromAddress}
        >
          {short(transfer.fromAddress)}
        </a>
      ),
    },
    {
      key: "tx",
      title: "Transaction",
      width: 130,
      render: (transfer) => (
        <a
          href={transfer.explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(styles.link, styles.mono)}
          title={transfer.txHash}
        >
          {short(transfer.txHash, 8, 6)} ↗
        </a>
      ),
    },
    {
      key: "resolved",
      title: "Decided by",
      width: 170,
      render: (transfer) =>
        transfer.resolvedBy ? (
          <span className={styles.stack}>
            <span>{transfer.resolvedBy}</span>
            <span className={styles.muted}>
              {dayjs(transfer.resolvedAt).format("DD.MM.YYYY HH:mm")}
            </span>
          </span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
  ];

  return (
    <Page
      fitScreen
      className={styles.page}
      title="Crypto payments"
      subtitle="USDT (TRC20) sent straight to the Ledger address: who paid, what was credited, and the AML screen of the sender."
      actions={<ModeControl />}
    >
      <div className={styles.filters}>
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cn(styles.filter, {
              [styles.filterActive]: filter === option.value,
            })}
            onClick={() => {
              setLimit(PAGE);
              setFilter(option.value);
            }}
          >
            {option.label}
          </button>
        ))}
        {data && (
          <span className={styles.count}>
            <CountUp to={total} /> transfers
          </span>
        )}
      </div>

      {error && !data ? (
        <p className={styles.empty}>Crypto payments could not be loaded.</p>
      ) : loading && !data ? (
        <p className={styles.empty}>Loading…</p>
      ) : (
        <DataTable
          id="crypto-payments"
          columns={columns}
          rows={transfers}
          rowKey={(transfer) => transfer.id}
          emptyText={active.empty}
          resetKey={filter}
        />
      )}

      {transfers.length < total && (
        <div className={styles.more}>
          <BasicButton
            variant="secondary"
            title={loading ? "Loading…" : "Load more"}
            onClick={() => setLimit(limit + PAGE)}
            disabled={loading}
          />
        </div>
      )}
    </Page>
  );
};

export default CryptoPayments;
